import { useTranslation } from 'react-i18next';
import { useState, ReactNode } from 'react';
import Modal from 'react-bootstrap/Modal';
import root from 'react-shadow';
import { useUser } from 'app/state/contexts';
import { useTemplates, useSelectedTemplates } from 'entities/template';
import { useCss } from 'entities/style';
import './PreviewModal.scss';

interface PreviewModalProps {
  show: boolean;
  onClose: () => void;
  documentId: string;
}

interface TemplateNode {
  type: string;
  className?: string;
  formatting?: { format?: string; alignment?: string };
  children?: TemplateNode[];
  text?: string;
  templateSuperId?: string;
}

const placeholderText: Record<string, string> = {
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  p: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
};

function renderNode(node: TemplateNode, index: number): ReactNode {
  if (!node) return null;

  if (node.type === 'paragraph' || node.type === 'heading') {
    const Tag = (node.formatting?.format || 'p') as React.ElementType;
    const className = node.formatting?.alignment || '';
    const rawText = node.children?.map((c) => c.text).join('') || '';
    const text = rawText.trim() || placeholderText[Tag as string] || '\u00A0';
    return (
      <Tag key={index} className={className || undefined}>
        {text}
      </Tag>
    );
  }

  if (node.type === 'layout') {
    return (
      <div key={index} className={node.className || ''}>
        {node.children?.map((child, i) => renderNode(child, i))}
      </div>
    );
  }

  if (node.type === 'page') {
    return (
      <div key={index} className={`page-content xrp-template-${node.templateSuperId}`}>
        {node.children?.map((child, i) => renderNode(child, i))}
      </div>
    );
  }

  // For text nodes or unknown types, just render children if any
  if (node.children) {
    return node.children.map((child, i) => renderNode(child, i));
  }

  return null;
}

export function PreviewModal({ show, onClose, documentId }: PreviewModalProps) {
  const { t } = useTranslation(['common', 'dc']);

  const { selectedOrganization } = useUser();
  const allTemplates = useTemplates({ organization: selectedOrganization, type: 'template', documentSuperId: documentId });
  const { data: selectedTemplates } = useSelectedTemplates(documentId);
  const css = useCss({ documentSuperId: documentId, documentRevisionId: undefined, useDraft: true, withStyle: undefined, embedd: undefined });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const templateList = (allTemplates.data || []).filter((t) => (selectedTemplates || []).includes(t.superId));
  const selectedTemplate = templateList[selectedIndex] || templateList[0];

  const pageStructure: TemplateNode | null = (selectedTemplate?.structure as TemplateNode | undefined) ?? null;

  return (
    <Modal show={show} onHide={onClose} size="xl" fullscreen="lg-down" dialogClassName="preview-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{t('dc:preview.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="preview-modal-body">
        {templateList.length > 1 && (
          <div className="preview-modal-sidebar">
            <div className="preview-modal-sidebar__label">{t('dc:preview.templates')}</div>
            {templateList.map((t, i) => (
              <button
                key={t.superId || i}
                className={`preview-modal-sidebar__item ${i === selectedIndex ? 'preview-modal-sidebar__item--active' : ''}`}
                onClick={() => setSelectedIndex(i)}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}
        <div className="preview-modal-main">
          {css.isLoading && <div className="text-muted p-3">{t('common:loading')}</div>}
          {css.data && pageStructure && (
            <div className="preview-modal-page">
              <root.div className="preview-shadow-host">
                <style>{css.data.shadowedCss}</style>
                <style>{`
                  .page-content > div { background: rgba(59, 130, 246, 0.06); outline: 1px dashed rgba(59, 130, 246, 0.25); }
                  .page-content > div > div { background: rgba(16, 185, 129, 0.08); outline: 1px dashed rgba(16, 185, 129, 0.3); }
                  .page-content > div > div > div { background: rgba(245, 158, 11, 0.08); outline: 1px dashed rgba(245, 158, 11, 0.3); }
                  .page-content p, .page-content h1, .page-content h2, .page-content h3,
                  .page-content h4, .page-content h5, .page-content h6 { background: rgba(139, 92, 246, 0.06); }
                `}</style>
                <div className={`page-content xrp-template-${selectedTemplate?.superId}`}>
                  {pageStructure.children?.map((child: TemplateNode, i: number) => renderNode(child, i))}
                  <h1>Heading 1</h1>
                  <h2>Heading 2</h2>
                  <h3>Heading 3</h3>
                  <h4>Heading 4</h4>
                  <h5>Heading 5</h5>
                  <h6>Heading 6</h6>
                  <p>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
                    veniam, quis nostrud exercitation ullamco laboris.
                  </p>
                </div>
              </root.div>
            </div>
          )}
          {!css.isLoading && !pageStructure && <div className="text-muted p-3">{t('dc:preview.no-template')}</div>}
        </div>
      </Modal.Body>
    </Modal>
  );
}
